import { preferences } from '@apexx/storage';

import { getKeycloakStorageKey } from './keycloakConfig';

/** Serialized Keycloak tokens persisted across app restarts. */
export type PersistedKeycloakSession = {
	token: string;
	refreshToken: string;
	idToken?: string;
	savedAt: number;
};

type JwtPayload = {
	exp?: number;
	iat?: number;
	typ?: string;
	sid?: string;
	session_state?: string;
	scope?: string;
	sub?: string;
};

/** Safe refresh-token fields for Sentry (never includes the token string). */
export type RefreshTokenDebugMeta = {
	refreshTokenTyp?: string;
	sid?: string;
	sessionState?: string;
	scope?: string;
	exp?: number;
	iat?: number;
	expiresInSec?: number;
	ageSec?: number;
};

let sessionCache: PersistedKeycloakSession | null | undefined;
let preloadPromise: Promise<void> | null = null;

/** Decodes the JWT payload segment without verifying the signature. */
export function decodeJwtPayload(token: string): JwtPayload | undefined {
	const parts = token.split('.');
	if (parts.length < 2) {
		return undefined;
	}

	try {
		const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
		const parsed = JSON.parse(payload) as unknown;

		return typeof parsed === 'object' && parsed !== null ? (parsed as JwtPayload) : undefined;
	} catch {
		return undefined;
	}
}

/**
 * Extracts non-secret refresh-token claims for auth diagnostics.
 * Use on restore/refresh failures to distinguish offline vs online sessions.
 */
export function getRefreshTokenDebugMeta(refreshToken: string | undefined): RefreshTokenDebugMeta {
	if (!refreshToken) {
		return {};
	}

	const payload = decodeJwtPayload(refreshToken);
	if (!payload) {
		return {};
	}

	const now = Math.floor(Date.now() / 1000);
	const meta: RefreshTokenDebugMeta = {};

	if (typeof payload.typ === 'string' && payload.typ !== '') {
		meta.refreshTokenTyp = payload.typ;
	}
	if (typeof payload.sid === 'string' && payload.sid !== '') {
		meta.sid = payload.sid;
	}
	if (typeof payload.session_state === 'string' && payload.session_state !== '') {
		meta.sessionState = payload.session_state;
	}
	if (typeof payload.scope === 'string' && payload.scope !== '') {
		meta.scope = payload.scope;
	}
	if (typeof payload.exp === 'number') {
		meta.exp = payload.exp;
		meta.expiresInSec = payload.exp - now;
	}
	if (typeof payload.iat === 'number') {
		meta.iat = payload.iat;
		meta.ageSec = now - payload.iat;
	}

	return meta;
}

function tokenExpiresAtSeconds(token: string): number | undefined {
	const payload = decodeJwtPayload(token);

	return typeof payload?.exp === 'number' ? payload.exp : undefined;
}

/** Returns whether the access token is still valid for at least `minValiditySeconds`. */
export function isAccessTokenUsable(token: string, minValiditySeconds = 0): boolean {
	const expiresAt = tokenExpiresAtSeconds(token);
	if (expiresAt === undefined) {
		return true;
	}

	return expiresAt - minValiditySeconds > Math.floor(Date.now() / 1000);
}

/** Returns whether the persisted refresh token has not expired. */
export function isRefreshTokenUsable(session: PersistedKeycloakSession): boolean {
	return isAccessTokenUsable(session.refreshToken, 0);
}

function parsePersistedSession(raw: string | null): PersistedKeycloakSession | null {
	if (!raw) {
		return null;
	}

	try {
		const parsed = JSON.parse(raw) as Partial<PersistedKeycloakSession>;
		if (
			typeof parsed.token !== 'string' ||
			parsed.token === '' ||
			typeof parsed.refreshToken !== 'string' ||
			parsed.refreshToken === ''
		) {
			return null;
		}

		if (!isRefreshTokenUsable(parsed as PersistedKeycloakSession)) {
			return null;
		}

		return {
			token: parsed.token,
			refreshToken: parsed.refreshToken,
			idToken: typeof parsed.idToken === 'string' ? parsed.idToken : undefined,
			savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : Date.now(),
		};
	} catch {
		return null;
	}
}

async function readSession(): Promise<PersistedKeycloakSession | null> {
	return parsePersistedSession(await preferences.getItem(getKeycloakStorageKey()));
}

async function writeSession(raw: string | null): Promise<void> {
	const key = getKeycloakStorageKey();

	if (raw === null) {
		await preferences.removeItem(key);

		return;
	}

	await preferences.setItem(key, raw);
}

/**
 * Loads persisted tokens into memory before {@link loadPersistedKeycloakSession}.
 */
export async function preloadPersistedKeycloakSession(): Promise<void> {
	if (sessionCache !== undefined) {
		return;
	}

	if (preloadPromise) {
		await preloadPromise;

		return;
	}

	preloadPromise = (async () => {
		sessionCache = await readSession();
	})();

	try {
		await preloadPromise;
	} finally {
		preloadPromise = null;
	}
}

/** Loads a persisted Keycloak session, or `null` when absent or expired. */
export function loadPersistedKeycloakSession(): PersistedKeycloakSession | null {
	if (sessionCache !== undefined) {
		return sessionCache;
	}

	return null;
}

/** Persists the current Keycloak tokens for the next app launch. */
export function savePersistedKeycloakSession(session: PersistedKeycloakSession): void {
	sessionCache = session;
	void writeSession(JSON.stringify(session));
}

/** Removes the persisted Keycloak session. */
export function clearPersistedKeycloakSession(): void {
	sessionCache = null;
	void writeSession(null);
}

/** @internal Resets module state between unit tests. */
export function resetKeycloakTokenStorageForTests(): void {
	sessionCache = undefined;
	preloadPromise = null;
}
